/** The old code's one-pixel column edges (`chart.tsx:41-58`). See `gridBackground`. */
const EDGE_PX = 1;
const DAYS_PER_WEEK = 7;

const WEEK_LINE = 'var(--gantt-grid-line)';

/**
 * The day grid as three stacked repeating gradients rather than one DOM node per day. Carried
 * over from `components/gantt-chart/components/chart.tsx:28-60`, with three changes: the weekend
 * comes from the working week rather than a locale-formatted weekday name, the phase is a
 * parameter instead of being read off a `Date` inside a ref callback, and every horizontal
 * dimension is derived from `pxPerDay`.
 *
 * `pxPerDay` is the scale's own column width (`TimeScale.pxPerDay`) and is the ONLY source of
 * horizontal geometry here. It used to be a hardcoded 48, restated a fourth time after
 * `time-scale.ts`, the bar CSS and `--gantt-px-per-day` — at any zoom but `day` the columns,
 * week separators and grid lines would have stayed at 48px while every bar and tick moved.
 * Callers must also key their memo on it. Expressed in px rather than the old `4.7rem`/`4.8rem`
 * literals for the same reason: `pxPerDay` is a pixel value, so mixing in a rem literal would
 * silently re-introduce a dependency on the 62.5% rem base.
 *
 * The three layers are stacked, and the FIRST layer is the topmost one. The column colours are
 * opaque, so a column that filled its slot edge to edge would hide both layers under it and the
 * chart would lose every day separator. That is why each column paints only `1px … pxPerDay-1px`
 * of its slot: the remaining pixel at each end is where the dash layer and the grid-line layer
 * show through, which is what draws the dashed day separator the chart has today. The two edges
 * that are solid instead of transparent bracket the boundary between the non-working run and the
 * working run — Sunday|Monday for a Mon-Fri week, hard-coded as `index === 0` / `index === 1` in
 * the old code and derived from `workingWeek` here.
 *
 * `startDayOfWeek` is `dayOfWeekOfEpochDay(range.startDay)` — 0 = Sunday.
 */
export const gridBackground = (
  startDayOfWeek: number,
  workingWeek: readonly number[],
  pxPerDay: number,
): { background: string; backgroundSize: string } => {
  const isWorking = (dayOfWeek: number) => workingWeek.includes(((dayOfWeek % 7) + 7) % 7);

  const slots = Array.from({ length: DAYS_PER_WEEK }, (_, offset) => {
    const dayOfWeek = (startDayOfWeek + offset) % 7;
    const working = isWorking(dayOfWeek);
    const from = offset * pxPerDay;
    const to = from + pxPerDay;

    // The working run's first day carries a solid leading edge, the non-working run's last day a
    // solid trailing edge; together they read as one two-pixel week separator.
    const leading = working && !isWorking(dayOfWeek - 1) ? WEEK_LINE : 'transparent';
    const trailing = !working && isWorking(dayOfWeek + 1) ? WEEK_LINE : 'transparent';
    const fill = `var(--gantt-${working ? 'weekday' : 'weekend'}-bg)`;

    return [
      `${leading} ${from}px ${from + EDGE_PX}px`,
      `${fill} ${from + EDGE_PX}px ${to - EDGE_PX}px`,
      `${trailing} ${to - EDGE_PX}px ${to}px`,
    ].join(', ');
  });

  const weekPx = pxPerDay * DAYS_PER_WEEK;
  const linePx = pxPerDay - EDGE_PX;

  return {
    background: [
      `repeating-linear-gradient(90deg, ${slots.join(', ')})`,
      'repeating-linear-gradient(to bottom, transparent 0 0.3rem, var(--gantt-grid-dash) 0.3rem 0.5rem)',
      `repeating-linear-gradient(90deg, transparent 0 ${linePx}px, ${WEEK_LINE} ${linePx}px ${pxPerDay}px)`,
    ].join(', '),
    // The middle layer is the horizontal dashed row separator: vertical geometry, unrelated to
    // the column width, so it stays in rem.
    backgroundSize: `${weekPx}px 100%, 100% 0.5rem, ${pxPerDay}px 100%`,
  };
};
